/** One activity in the editor tray: everything a customer may change about it. */

import { useState } from "react";
import type { ActivityRoleAssignment } from "@nebulize/sdk";

import {
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Select,
  Stack,
  StatusBadge,
  Toolbar,
} from "@/components/ui";

import {
  useActivityRoles,
  useAssignRole,
  useMembers,
  useRevokeRoleAssignment,
  useSetVersionPolicy,
  useVersions,
  type VersionPolicy,
} from "../activities/lib/queries";
import { ActivityVerbRow } from "../components/ActivityVerbRow";
import { GatesEditor } from "../components/GatesEditor";
import { Requirements } from "../components/Requirements";
import { RunBadge } from "../components/RunBadge";
import { SettingControl } from "../components/SettingControl";
import { Mono, Muted } from "../components/Text";
import { lifecycleOf, rolesOf, settingsOf } from "../lib/catalog";
import {
  useActivityVerb,
  useMoveVersion,
  useRemoveActivity,
  useRenameActivity,
  useSetting,
} from "../spaces/lib/queries";
import type { SpaceActivities } from "../spaces/lib/queries";
import type { AddedActivityRow, CatalogEntry } from "../types";

import { versionPending } from "./lib/readiness";
import { Section } from "./tray/Section";

/** The customer's name for this instance, and which published version it runs. */
function Identity({ added }: { readonly added: AddedActivityRow }) {
  const [name, setName] = useState(added.name);
  const [version, setVersion] = useState(added.activity_version);
  const [drop, setDrop] = useState(false);
  const rename = useRenameActivity(added.added_activity_id);
  const move = useMoveVersion(added.added_activity_id);
  const versions = useVersions(added.activity_id);
  const policy = useSetVersionPolicy();
  const verb = useActivityVerb();
  const pending = versionPending(added);

  const published = (versions.data ?? []).map((one) => one.version);
  const options = published.includes(added.activity_version)
    ? published
    : [added.activity_version, ...published];

  const newest = versions.data?.[0];
  const updateAvailable =
    newest !== undefined &&
    newest.version !== added.activity_version &&
    (newest.availability === undefined || newest.availability === "published");
  const currentPolicy: VersionPolicy = added.version_policy ?? "pin";

  return (
    <Stack gap={5}>
      <Field label="Name">
        <Input
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
          onBlur={() => {
            if (name.trim() !== "" && name !== added.name) rename.mutate(name.trim());
          }}
        />
      </Field>
      {rename.isError ? <StatusBadge tone="danger">{rename.error.message}</StatusBadge> : null}

      <Section title="Version">
        <Stack gap={3}>
          {pending === undefined ? null : (
            <div className="cx-brings">
              <span className="cx-key">Version</span>
              <StatusBadge tone="warning">{`running ${pending.running}`}</StatusBadge>
              <Muted small>{`configured ${pending.configured}`}</Muted>
              <Button
                size="sm"
                variant="secondary"
                loading={verb.isPending}
                onClick={() => {
                  verb.mutate({ row: added, verb: "Restart" });
                }}
              >
                Restart
              </Button>
            </div>
          )}
          <Field label="Version">
            <Select
              value={version}
              options={options.map((one) => ({ value: one, label: one }))}
              onChange={(event) => {
                setVersion(event.target.value);
              }}
            />
          </Field>
          {updateAvailable ? (
            <StatusBadge tone="info">{`${newest?.version ?? ""} available`}</StatusBadge>
          ) : null}
          <Checkbox
            label="Drop settings the chosen version no longer declares"
            checked={drop}
            onChange={setDrop}
          />
          <Toolbar>
            <Button
              size="sm"
              variant="secondary"
              loading={move.isPending}
              disabled={version === added.activity_version}
              onClick={() => {
                move.mutate({ version, dropUnsupported: drop });
              }}
            >
              Move
            </Button>
            {updateAvailable && currentPolicy === "track" ? (
              <StatusBadge tone="info">{`${newest?.version ?? ""} available`}</StatusBadge>
            ) : null}
          </Toolbar>
          <Field label="Version policy">
            <Select
              value={currentPolicy}
              disabled={policy.isPending}
              options={[
                { value: "pin", label: "pin" },
                { value: "track", label: "track" },
              ]}
              onChange={(event) => {
                const next = event.target.value as VersionPolicy;
                if (next !== currentPolicy) {
                  policy.mutate({ addedActivityId: added.added_activity_id, versionPolicy: next });
                }
              }}
            />
          </Field>
          {move.isError ? <StatusBadge tone="danger">{move.error.message}</StatusBadge> : null}
          {policy.isError ? <StatusBadge tone="danger">{policy.error.message}</StatusBadge> : null}
          {verb.isError ? <StatusBadge tone="danger">{verb.error.message}</StatusBadge> : null}
        </Stack>
      </Section>
    </Stack>
  );
}

/** Who reaches which of this activity's own canvases. */
function Roles({
  added,
  entry,
  spaceId,
}: {
  readonly added: AddedActivityRow;
  readonly entry: CatalogEntry;
  readonly spaceId: string;
}) {
  const declared = rolesOf(entry);
  const assignments = useActivityRoles(added.added_activity_id);
  const assign = useAssignRole(added.added_activity_id);
  const revoke = useRevokeRoleAssignment(added.added_activity_id);
  const members = useMembers(spaceId);

  const [role, setRole] = useState(declared[0] ?? "");
  const [holder, setHolder] = useState("");

  if (declared.length === 0) return null;

  const held = assignments.data ?? [];

  return (
    <Section title="Who reaches it">
      <Stack gap={3}>
        {held.length === 0 ? (
          <Muted small>Nobody assigned</Muted>
        ) : (
          held.map((one: ActivityRoleAssignment) => (
            <div className="cx-row" key={one.assignment_id}>
              <div className="cx-row-id">
                <div>
                  <div className="cx-node-name">{one.role}</div>
                  <Muted small>
                    {one.catch_all === true ? "everyone else" : (one.account_id ?? "")}
                  </Muted>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Revoke ${one.role}`}
                loading={revoke.isPending}
                onClick={() => {
                  revoke.mutate(one.assignment_id);
                }}
              >
                Revoke
              </Button>
            </div>
          ))
        )}

        <Field label="Role">
          <Select
            value={role}
            options={declared.map((one) => ({ value: one, label: one }))}
            onChange={(event) => {
              setRole(event.target.value);
            }}
          />
        </Field>
        <Field label="Held by">
          <Select
            value={holder}
            options={[
              { value: "", label: "everyone else" },
              ...(members.data ?? []).map((one) => ({
                value: one.account_id,
                label: one.account_id,
              })),
            ]}
            onChange={(event) => {
              setHolder(event.target.value);
            }}
          />
        </Field>
        {assign.isError ? <StatusBadge tone="danger">{assign.error.message}</StatusBadge> : null}
        <Toolbar>
          <Button
            size="sm"
            variant="secondary"
            loading={assign.isPending}
            disabled={role === ""}
            onClick={() => {
              assign.mutate(
                holder === ""
                  ? { role, catch_all: true }
                  : { role, account_id: holder },
              );
            }}
          >
            Assign
          </Button>
        </Toolbar>
      </Stack>
    </Section>
  );
}

export interface ActivityPanelProps {
  readonly added: AddedActivityRow;
  readonly activities: SpaceActivities;
  readonly spaceId: string;
  readonly onClear: () => void;
}

export function ActivityPanel({ added, activities, spaceId, onClear }: ActivityPanelProps) {
  const entry = activities.entryFor(added);
  const setting = useSetting(added.added_activity_id);
  const remove = useRemoveActivity();
  const [removing, setRemoving] = useState(false);

  return (
    <Stack gap={5}>
      <div className="cx-editor-head">
        <div>
          <div className="cx-node-name">{added.name}</div>
          <Muted small>
            <Mono>{added.activity_id}</Mono>
          </Muted>
        </div>
        <Button size="sm" variant="ghost" onClick={onClear}>
          Close
        </Button>
      </div>

      <Toolbar>
        <RunBadge mount={added.mount} />
        <ActivityVerbRow
          added={added}
          lifecycle={entry === undefined ? undefined : lifecycleOf(entry)}
        />
      </Toolbar>

      <Identity added={added} />

      {entry === undefined ? (
        <EmptyState bare headline="No manifest for this version" />
      ) : (
        <>
          <Section title="Needs">
            <Requirements added={added} entry={entry} spaceId={spaceId} />
          </Section>

          {settingsOf(entry).length === 0 ? null : (
            <Section title="Settings">
              <Stack gap={4}>
                {setting.isError ? (
                  <StatusBadge tone="danger">
                    {`${setting.variables?.key ?? "setting"}: ${setting.error.message}`}
                  </StatusBadge>
                ) : null}
                {settingsOf(entry).map((field) => (
                  <SettingControl
                    key={field.key}
                    field={field}
                    value={added.settings[field.key] ?? field.default}
                    onChange={(next) => {
                      setting.mutate({ key: field.key, value: next });
                    }}
                  />
                ))}
              </Stack>
            </Section>
          )}

          <Roles added={added} entry={entry} spaceId={spaceId} />
        </>
      )}

      <GatesEditor addedActivityId={added.added_activity_id} title="Gates" />

      <Section title="Remove">
        <Toolbar>
          <Button
            size="sm"
            variant="destructive"
            loading={remove.isPending}
            onClick={() => {
              setRemoving(true);
            }}
          >
            Remove from this space
          </Button>
        </Toolbar>
        {remove.isError ? <StatusBadge tone="danger">{remove.error.message}</StatusBadge> : null}
      </Section>

      <ConfirmDialog
        open={removing}
        title={`Remove ${added.name}`}
        description="Its settings, its bindings and where it sits on every canvas go with it."
        confirmLabel="Remove"
        confirmPhrase={added.name}
        loading={remove.isPending}
        onCancel={() => {
          setRemoving(false);
        }}
        onConfirm={() => {
          remove.mutate(added.added_activity_id, {
            onSuccess: () => {
              setRemoving(false);
              onClear();
            },
          });
        }}
      />
    </Stack>
  );
}
